/**
 * SWAYM Header Slider Web Component
 * Standard web component wrapper managing Swiper instance with minimal fade-in transitions,
 * video autoplay/pause control, and Shopify Theme Editor live updates.
 */

if (!customElements.get('swaym-header-slider')) {
  class SwaymHeaderSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
    }

    connectedCallback() {
      this.initSlider();
      this.bindEvents();
    }

    initSlider() {
      const container = this.querySelector('.swiper');
      if (!container) return;

      const slides = this.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;
      const autoplayEnabled = this.dataset.autoplay === 'true' && slideCount > 1;
      const autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '5', 10) * 1000;
      const speed = parseInt(this.dataset.transitionSpeed || '800', 10);

      // If only 1 slide, ensure active slide animation triggers immediately
      if (slideCount <= 1 && slides[0]) {
        slides[0].classList.add('swiper-slide-active');
      }

      const swiperConfig = {
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        speed: speed,
        loop: slideCount > 1 && this.dataset.loop === 'true',
        allowTouchMove: slideCount > 1,
        grabCursor: slideCount > 1,
        autoplay: autoplayEnabled ? {
          delay: autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        } : false,
        pagination: slideCount > 1 ? {
          el: this.querySelector('.swiper-pagination'),
          clickable: true
        } : false,
        navigation: slideCount > 1 ? {
          nextEl: this.querySelector('.swaym-slider-arrow-next'),
          prevEl: this.querySelector('.swaym-slider-arrow-prev')
        } : false,
        on: {
          init: () => {
            this.handleVideoPlayback();
          },
          slideChange: () => {
            this.handleVideoPlayback();
          }
        }
      };

      if (typeof Swiper !== 'undefined') {
        this.swiper = new Swiper(container, swiperConfig);
      } else {
        // Fallback retry if Swiper JS is loading asynchronously
        window.addEventListener('load', () => {
          if (typeof Swiper !== 'undefined') {
            this.swiper = new Swiper(container, swiperConfig);
          }
        });
      }
    }

    handleVideoPlayback() {
      const slides = this.querySelectorAll('.swiper-slide');
      if (!slides.length) return;

      const activeIndex = this.swiper ? this.swiper.activeIndex : 0;

      slides.forEach((slide, index) => {
        const videos = slide.querySelectorAll('video');
        if (index === activeIndex || slides.length === 1) {
          videos.forEach(video => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // Autoplay was prevented
              });
            }
          });
        } else {
          videos.forEach(video => {
            video.pause();
          });
        }
      });
    }

    bindEvents() {
      // Shopify Theme Editor Integration
      document.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          this.initSlider();
        }
      });

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.swiper) return;
        const blockSlide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        if (blockSlide) {
          const index = Array.from(this.querySelectorAll('.swiper-slide')).indexOf(blockSlide);
          if (index !== -1) {
            this.swiper.slideTo(index);
          }
        }
      });
    }

    disconnectedCallback() {
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
      }
    }
  }

  customElements.define('swaym-header-slider', SwaymHeaderSlider);
}
